import { useCallback, useEffect, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import type { SerializedAssetShare } from 'fym-shared/src/music24/assetShares';
import {
  useCreateAssetShareMutation,
  useDeleteAssetShareMutation,
  useGetAssetShareQuery,
} from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import {
  INITIAL_SHARE_UI,
  ShareBoxStatus,
  ShareErrorKind,
  shareBoxStatus,
  shareErrorKind,
  shareUiReducer,
} from './assetShareState';
import { trackEvent } from '../../../analytics/sml/analytics';
import { AssetViewerItem } from './viewerItems';
import { viewerMessages } from './viewerMessages';

export interface AssetShareModel {
  status: ShareBoxStatus;
  share: SerializedAssetShare | null;
  error: ShareErrorKind | null;
  isCreating: boolean;
  isTurningOff: boolean;
  /** True a moment after a copy: the Copy button reads "Copied". */
  justCopied: boolean;
  confirmOpen: boolean;
  create: () => void;
  copy: () => void;
  askTurnOff: () => void;
  cancelTurnOff: () => void;
  turnOff: () => void;
}

const COPIED_MS = 2000;

function copyToClipboard(text: string): void {
  if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
    return;
  }
  navigator.clipboard.writeText(text).catch(() => undefined);
}

/**
 * The share box of one asset (designs a1..a5). The GET runs when the viewer opens the asset, so
 * a shared asset opens in the a2 state. "Share link" POSTs, copies the link at once and shows
 * "Link copied". "Turn off the link" asks first (a3), then DELETEs (a4, "Link turned off").
 * `skip` (the demo artist) reads nothing.
 */
export function useAssetShare(input: {
  accountId: number;
  item: AssetViewerItem;
  skip: boolean;
  onToast: (text: string) => void;
}): AssetShareModel {
  const { accountId, item, skip, onToast } = input;
  const intl = useIntl();
  const args = { accountId, kind: item.kind, assetUuid: item.assetUuid };
  const query = useGetAssetShareQuery(args, { skip });
  const [createShare, created] = useCreateAssetShareMutation();
  const [deleteShare, deleted] = useDeleteAssetShareMutation();
  const [ui, dispatch] = useReducer(shareUiReducer, INITIAL_SHARE_UI);
  const [justCopied, setJustCopied] = useState(false);

  // A move to another asset starts from its own state.
  useEffect(() => {
    dispatch({ type: 'reset' });
    setJustCopied(false);
  }, [item.key]);
  useEffect(() => {
    if (!justCopied) {
      return undefined;
    }
    const timer = setTimeout(() => setJustCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [justCopied]);

  const share = ui.turnedOff ? null : (query.data?.share ?? null);
  const status = shareBoxStatus({ isLoading: query.isLoading, share, turnedOff: ui.turnedOff });

  const markCopied = useCallback(
    (url: string) => {
      copyToClipboard(url);
      setJustCopied(true);
      onToast(intl.formatMessage(viewerMessages.toastCopied));
      trackEvent('asset_shared', { assetType: item.themeId, target: 'copyLink' });
    },
    [intl, item.themeId, onToast]
  );

  const create = useCallback(() => {
    dispatch({ type: 'createStarted' });
    createShare({ accountId, kind: item.kind, assetUuid: item.assetUuid })
      .then((result) => {
        if (isSuccessfulResult(result) && result.data.share !== null) {
          dispatch({ type: 'created' });
          markCopied(result.data.share.url);
          return;
        }
        dispatch({ type: 'createFailed', error: shareErrorKind('error' in result ? result.error : null) });
      })
      .catch(() => dispatch({ type: 'createFailed', error: 'failed' }));
  }, [accountId, createShare, item.assetUuid, item.kind, markCopied]);

  const copy = useCallback(() => {
    if (share !== null) {
      markCopied(share.url);
    }
  }, [markCopied, share]);

  const turnOff = useCallback(() => {
    deleteShare({ accountId, kind: item.kind, assetUuid: item.assetUuid })
      .then((result) => {
        if (isSuccessfulResult(result)) {
          dispatch({ type: 'turnedOff' });
          onToast(intl.formatMessage(viewerMessages.toastTurnedOff));
          return;
        }
        dispatch({ type: 'turnOffFailed' });
      })
      .catch(() => dispatch({ type: 'turnOffFailed' }));
  }, [accountId, deleteShare, intl, item.assetUuid, item.kind, onToast]);

  const askTurnOff = useCallback(() => dispatch({ type: 'askTurnOff' }), []);
  const cancelTurnOff = useCallback(() => dispatch({ type: 'cancelTurnOff' }), []);

  return {
    status,
    share,
    error: ui.error,
    isCreating: created.isLoading,
    isTurningOff: deleted.isLoading,
    justCopied,
    confirmOpen: ui.confirmOpen,
    create,
    copy,
    askTurnOff,
    cancelTurnOff,
    turnOff,
  };
}
